import { Input, ToastMessage } from '@cokitchen/cokitchen-components';
import React, { useEffect, useRef, useState } from 'react';
import { MealType } from 'utils/types';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';

type Props = {
  mealList: MealType[];
  setMealList: (mealList: MealType[]) => void;
  mealLoading: boolean;
};

type EditableInputProps = {
  edit: boolean;
  inputRef?: React.RefObject<HTMLInputElement> | ((instance: HTMLInputElement | null) => void);
  readOnly: boolean;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const EditableInput: React.FC<EditableInputProps> = ({
  edit,
  inputRef,
  readOnly,
  value,
  onChange
}) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const ref = typeof inputRef === 'function' ? { current: null } : inputRef || internalRef;
  useEffect(() => {
    if (edit && ref && 'current' in ref && ref.current) {
      ref.current.focus();
    }
  }, [edit, ref]);

  return (
    <input
      ref={ref as React.RefObject<HTMLInputElement>}
      type='text'
      name=''
      id=''
      className='bg-gray-14 w-[56px] outline-none border-none'
      readOnly={readOnly}
      value={value}
      onChange={onChange}
    />
  );
};
const CategoryPricingCard = ({ mealList, setMealList, mealLoading }: Props) => {
  const [editId, setEditId] = useState<string | null>(null);
  const [editAll, setEditAll] = useState(false);
  const [mealAltered, setMealAltered] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const updateMeal = (id: string, value: string) => {
    setMealList(
      mealList.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            amount: value
          };
        }
        return item;
      })
    );
  };

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, inputRefs.current.length);
  }, []);

  const registerInputRef = (ref: HTMLInputElement | null) => {
    if (ref && !inputRefs.current.includes(ref)) {
      inputRefs.current.push(ref);
    }
  };
  const mealCategoryName = mealList?.[0]?.meal_category?.name;
  const unavailableMeals = mealList?.filter((item) => !item.active).length;

  const submitMeal = async () => {
    setLoading(true);
    const mealArray = mealList.map((item) => {
      return {
        meal_id: item.id,
        amount: item.amount
      };
    });
    try {
      await apiService.put('kitchen/meal/bulk', {
        data: {
          meals: mealArray
        }
      });
      toast(<ToastMessage type='success' message='Meals successfully updated' />);
      setMealAltered(false);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem updating meals' />);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='w-[452px] h-[585px] bg-gray-5 rounded-xl border border-[#FFFFFF0D] py-[35px] px-[20px]'>
      <div className='flex justify-between items-center'>
        <div>
          <p className='font-medium text-[20px] text-white capitalize'>
            {mealCategoryName ? mealCategoryName : 'Category'} {mealAltered ? '*' : ''}
          </p>
          <p className='text-sm font-medium text-[#FFFFFFBF]'>{mealList?.length} total items</p>
        </div>
        <button
          className='bg-white rounded-[40px] text-gray-5 py-2 px-4 cursor-pointer font-semibold text-sm'
          onClick={() => submitMeal()}
          disabled={!mealAltered || loading}>
          {loading ? 'SAVING' : ' SAVE CHANGES'}
        </button>
      </div>
      <div className='bg-white rounded-[8px] w-[412px] h-[424px] mt-5 overflow-y-scroll py-[20px] px-4'>
        <Input
          type='search'
          placeholder='Search Item'
          inputClass='!h-[40px]'
          value={filterValue}
          onChange={(e) => {
            setFilterValue(e.target.value);
          }}
        />
        <div className='flex justify-between items-center pb-3 border-b border-b-gray-2 my-4'>
          <div>
            <p className='font-medium'>{mealCategoryName}</p>
            <p className='text-sm text-gray-8'>
              {unavailableMeals} of {mealList?.length} unavailable
            </p>
          </div>
          <div
            className='underline text-sm text-blue-5 cursor-pointer'
            onClick={() => {
              setEditAll(!editAll);
              setEditId(null);
            }}>
            Edit all
          </div>
        </div>
        {mealLoading ? (
          <p>Loading...</p>
        ) : mealList.length === 0 ? (
          <p>No meals found</p>
        ) : (
          mealList
            .filter((item) => {
              return item.name.toLowerCase().includes(filterValue.toLowerCase());
            })
            .map((item) => (
              <div className='pb-4 border-b border-b-gray-2 mb-3' key={item.id}>
                <p className='text-sm'>{item.name}</p>
                <div className='flex justify-between mt-2'>
                  <div
                    className={`w-[98px] h-8 rounded-[20px] py-[7px] px-2 bg-gray-14 flex items-center gap-1 ${
                      editId === item.id ? 'border border-gray-4' : ''
                    }`}>
                    <p>&#8358; </p>
                    <EditableInput
                      edit={editId === item.id}
                      inputRef={registerInputRef}
                      readOnly={!editAll && editId !== item.id}
                      value={item.amount}
                      onChange={(e: { target: { value: string } }) => {
                        setMealAltered(true);
                        updateMeal(item.id, e.target.value);
                      }}
                    />
                  </div>
                  <div
                    className={`underline text-sm text-blue-5 cursor-pointer ${
                      editId === item.id ? 'hidden' : ''
                    }`}
                    onClick={() => {
                      setEditId(item.id);
                      setEditAll(false);
                    }}>
                    Edit
                  </div>
                  {editId === item.id && (
                    <div
                      className='px-4 py-1 rounded-[20px] border border-gray-4 cursor-pointer hover:bg-gray-1'
                      onClick={() => {
                        setMealAltered(true);
                        setEditId(null);
                        updateMeal(item.id, item.amount);
                      }}>
                      Save
                    </div>
                  )}
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
};

export default CategoryPricingCard;
